import { useState, useMemo } from 'react';

interface EnumOption {
  value: string;
  label: string;
  color?: string;
}

interface EnumFilterProps {
  options: EnumOption[];
  selected: string[];
  mode?: 'include' | 'exclude';
  showModeToggle?: boolean;
  onChange: (selected: string[], mode?: 'include' | 'exclude') => void;
}

export function EnumFilter({
  options,
  selected,
  mode = 'include',
  showModeToggle = false,
  onChange,
}: EnumFilterProps) {
  const [search, setSearch] = useState('');
  const [currentMode, setCurrentMode] = useState(mode);

  // Filter options by search
  const filteredOptions = useMemo(() => {
    if (!search) return options;
    const lower = search.toLowerCase();
    return options.filter((opt) => opt.label.toLowerCase().includes(lower));
  }, [options, search]);

  const handleToggle = (value: string) => {
    const newSelected = selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value];
    onChange(newSelected, showModeToggle ? currentMode : undefined);
  };

  const handleSelectAll = () => {
    if (selected.length === options.length) {
      onChange([], showModeToggle ? currentMode : undefined);
    } else {
      onChange(options.map((o) => o.value), showModeToggle ? currentMode : undefined);
    }
  };

  const handleModeChange = (newMode: 'include' | 'exclude') => {
    setCurrentMode(newMode);
    onChange(selected, newMode);
  };

  return (
    <div className="space-y-2">
      {/* Mode toggle for multi-enum */}
      {showModeToggle && (
        <div className="flex rounded-lg border border-sage-200 overflow-hidden mb-2">
          <button
            onClick={() => handleModeChange('include')}
            className={`flex-1 px-3 py-1.5 text-xs font-medium transition-colors ${
              currentMode === 'include'
                ? 'bg-forest-700 text-white'
                : 'bg-white text-sage-600 hover:bg-sage-50'
            }`}
          >
            Include
          </button>
          <button
            onClick={() => handleModeChange('exclude')}
            className={`flex-1 px-3 py-1.5 text-xs font-medium transition-colors ${
              currentMode === 'exclude'
                ? 'bg-red-600 text-white'
                : 'bg-white text-sage-600 hover:bg-sage-50'
            }`}
          >
            Exclude
          </button>
        </div>
      )}

      {/* Search input for long lists */}
      {options.length > 5 && (
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search..."
          className="w-full px-3 py-1.5 text-sm border border-sage-200 rounded-lg focus:border-forest-500 focus:outline-none"
        />
      )}

      {/* Select all */}
      <label className="flex items-center gap-2 px-1 py-1 cursor-pointer hover:bg-sage-50 rounded">
        <input
          type="checkbox"
          checked={selected.length === options.length && options.length > 0}
          onChange={handleSelectAll}
          className="rounded border-sage-300 text-forest-600 focus:ring-forest-500"
        />
        <span className="text-sm font-medium text-sage-700">Select All</span>
      </label>

      {/* Options list */}
      <div className="max-h-48 overflow-y-auto space-y-0.5">
        {filteredOptions.map((option) => (
          <label
            key={option.value}
            className="flex items-center gap-2 px-1 py-1 cursor-pointer hover:bg-sage-50 rounded"
          >
            <input
              type="checkbox"
              checked={selected.includes(option.value)}
              onChange={() => handleToggle(option.value)}
              className="rounded border-sage-300 text-forest-600 focus:ring-forest-500"
            />
            {option.color && (
              <span
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: option.color }}
              />
            )}
            <span className="text-sm text-forest-900 truncate">{option.label}</span>
          </label>
        ))}

        {filteredOptions.length === 0 && (
          <div className="text-sm text-sage-500 text-center py-2">No options found</div>
        )}
      </div>
    </div>
  );
}
